import type { SelectChangeEvent } from '@mui/material'
import type { GridFilterItem } from '@mui/x-data-grid'
import { gridFilterModelSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import GridSearchOutlined from '@/components/dataDisplay/GridSearchOutlined'
import GridToolbar from '@/components/dataDisplay/GridToolbar'
import CountryFilter from '@/components/datagrid/CountryFilter'
import CustomSelectField from '@/components/inputs/CustomSelectField'
import { FIELD_WIDTH } from '@/constants/layout'
import type { RegulationObject } from '@/features/portfolioHierarchy/utils/resolveRegulationObjects'
import { getFilterItemValue, updateDataGridFilter } from '@/utils/datagrid/filters'
import { filterUnique } from '@/utils/filterUnique'
import { sortOptionsAlphabetically } from '@/utils/sortOptionsAlphabetically'

/**
 * Module augmentation is necessary to use slotProps without TypeScript errors.
 */
declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides extends RegulationObjectsDataGridToolbarProps {}
}

export type RegulationObjectsDataGridToolbarProps = {
  regulationObjects: RegulationObject[] | null
}

const RegulationObjectsDataGridToolbar = ({ regulationObjects }: RegulationObjectsDataGridToolbarProps) => {
  const { t } = useTranslation()
  const apiRef = useGridApiContext()

  const filterModel = useGridSelector(apiRef, gridFilterModelSelector)
  const countryCode = getFilterItemValue<string>(filterModel, 'countryCode') ?? ''
  const priceArea = getFilterItemValue<string>(filterModel, 'priceArea') ?? ''
  const portfolioCode = getFilterItemValue<string>(filterModel, 'portfolioCode') ?? ''

  const handleFilterChange = (field: string, value: string) => {
    updateDataGridFilter(apiRef, field, { id: field, field, operator: 'equals', value })
  }

  const blankOption = { value: '', label: '-', id: `filter-item-blank` }

  const countryCodeOptions = useMemo(() => {
    const uniqueCountryCodes = new Set<CountryIdentifier>()
    for (const regulationObject of regulationObjects || []) {
      uniqueCountryCodes.add(regulationObject.countryCode.toLowerCase() as CountryIdentifier)
    }
    return Array.from(uniqueCountryCodes)
  }, [regulationObjects])

  const priceAreaOptions = useMemo(() => {
    const selectFields = (regulationObjects || []).map((regulationObject) => ({
      id: regulationObject.priceArea,
      value: regulationObject.priceArea,
      label: regulationObject.priceArea,
    }))
    return sortOptionsAlphabetically(filterUnique(selectFields))
  }, [regulationObjects])

  const portfolioCodeOptions = useMemo(() => {
    const selectFields = (regulationObjects || []).map((regulationObject) => ({
      id: regulationObject.portfolioCode,
      value: regulationObject.portfolioCode,
      label: regulationObject.portfolioCode,
    }))
    return sortOptionsAlphabetically(filterUnique(selectFields))
  }, [regulationObjects])

  return (
    <GridToolbar
      filters={
        <>
          <GridSearchOutlined label={t('common.navigation.search')} placeholder={t('portfolio_hierarchy.search')} />

          <CountryFilter
            countryOptions={countryCodeOptions}
            field="countryCode"
            value={countryCode}
            onChange={(items: GridFilterItem[]) => handleFilterChange('countryCode', items[0]?.value as string)}
          />
          <CustomSelectField
            data-testid="price-area-selector"
            id="priceArea"
            label={t('portfolio_hierarchy.portfolio.price_area')}
            options={[blankOption, ...priceAreaOptions]}
            sx={{ minWidth: FIELD_WIDTH }}
            value={priceArea}
            onChange={(event: SelectChangeEvent) => handleFilterChange('priceArea', event.target.value)}
          />
          <CustomSelectField
            data-testid="portfolio-code-selector"
            id="portfolioCode"
            label={t('portfolio_hierarchy.portfolio.code')}
            options={[blankOption, ...portfolioCodeOptions]}
            sx={{ minWidth: FIELD_WIDTH }}
            value={portfolioCode}
            onChange={(event: SelectChangeEvent) => handleFilterChange('portfolioCode', event.target.value)}
          />
        </>
      }
    />
  )
}

export default RegulationObjectsDataGridToolbar
